import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { ServicePlanTerm } from 'src/features/servicePlanTerms/utils/types';
import { servicePlanTermsMessages } from '../../utils/messages';
import { LIST_SERVICE_PLAN_TERMS_RESOURCE } from 'src/features/servicePlanTerms/api/useListServicePlanTerms';
import { useDeleteServicePlanTerm } from 'src/features/servicePlanTerms/api/useDeleteServicePlanTerm';
import { useParams } from 'react-router-dom';
import { useOnError } from 'src/utils/errors';

interface DeleteServicePlanTermFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanTerm: ServicePlanTerm;
    children?: React.ReactNode;
}

function DeleteServicePlanTermFormModal<TData>({
    selectedServicePlanTerm,
    children,
}: DeleteServicePlanTermFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { servicePlanId = '' } = useParams();

    const onError = useOnError();

    const { mutate: deleteServicePlanTerm } = useDeleteServicePlanTerm({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_TERMS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(servicePlanTermsMessages.deleteServicePlanTermSuccess),
                description: formatMessage(servicePlanTermsMessages.deleteServicePlanTermSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...servicePlanTermsMessages.deleteServicePlanTerm },
                subTitle: {
                    ...servicePlanTermsMessages.deleteServicePlanTermSubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...servicePlanTermsMessages.confirmDeleteServicePlanTerm },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: servicePlanTermsMessages.deleteServicePlanTerm,
                callbackFn: () => {
                    return (
                        deleteServicePlanTerm({
                            termId: selectedServicePlanTerm.id,
                            servicePlanId: servicePlanId,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteServicePlanTermFormModal;
